import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Clock, Send, SkipForward, ExternalLink, MessageSquare, Loader2,
  CheckCircle, XCircle, AlertCircle, Eye, RefreshCw, Play
} from 'lucide-react';

const EVENT_LABELS = {
  unpaid_reminder: { label: 'Belum Bayar', color: 'bg-red-100 text-red-700' },
  post_delivery_review: { label: 'Review', color: 'bg-amber-100 text-amber-700' },
  repeat_order: { label: 'Repeat Order', color: 'bg-blue-100 text-blue-700' },
  cart_abandoned: { label: 'Cart Abandoned', color: 'bg-purple-100 text-purple-700' },
};

const STATUS_BADGES = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-600', icon: Clock },
  due: { label: 'Due Now', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  sent: { label: 'Terkirim', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  failed: { label: 'Gagal', color: 'bg-red-100 text-red-700', icon: XCircle },
  skipped: { label: 'Dilewati', color: 'bg-slate-100 text-slate-400', icon: SkipForward },
  canceled: { label: 'Dibatalkan', color: 'bg-slate-100 text-slate-400', icon: XCircle },
};

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function timeUntil(iso) {
  if (!iso) return '';
  const diff = new Date(iso) - new Date();
  if (diff <= 0) return 'Sekarang';
  const hours = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}h ${hours % 24}j lagi`;
  }
  return hours > 0 ? `${hours}j ${mins}m lagi` : `${mins}m lagi`;
}

export default function FollowUpQueue() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_pending: 0, due_now: 0, sent_today: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [hasGateway, setHasGateway] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    try {
      const [qRes, sRes] = await Promise.all([
        api.get('/tenant/followup-queue', { params: { page, limit: 20, status: filterStatus, event: filterEvent } }),
        api.get('/tenant/followup-queue/stats')
      ]);
      setItems(qRes.data.items || []);
      setTotal(qRes.data.total || 0);
      setHasGateway(qRes.data.has_wa_gateway);
      setStats(sRes.data);
    } catch (err) {
      console.error('Fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterEvent]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSend = async (item) => {
    if (!item.customer_phone) {
      showToast('Nomor HP customer tidak tersedia. Tidak bisa kirim WA.', 'error');
      return;
    }
    setSendingId(item.id);
    try {
      const { data } = await api.post(`/tenant/followup-queue/${item.id}/send`);
      if (data.wa_link) {
        window.open(data.wa_link, '_blank', 'noopener,noreferrer');
        showToast('Link WA dibuka di tab baru. Kirim pesan secara manual.');
      } else if (data.sent_via === 'starsender' || data.sent_via === 'responic') {
        showToast('Pesan berhasil dikirim via Responic!');
      }
      await fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Gagal mengirim follow-up';
      showToast(msg, 'error');
    } finally {
      setSendingId(null);
    }
  };

  const handleSkip = async (id) => {
    try {
      await api.post(`/tenant/followup-queue/${id}/skip`);
      showToast('Follow-up dilewati');
      await fetchData();
    } catch (err) {
      showToast('Gagal melewati follow-up', 'error');
    }
  };

  const handleProcessAll = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post('/tenant/followup-queue/process');
      if (data.manual_links && data.manual_links.length > 0) {
        window.open(data.manual_links[0].wa_link, '_blank', 'noopener,noreferrer');
      }
      showToast(`Diproses: ${data.sent || 0} terkirim, ${data.failed || 0} gagal`);
      await fetchData();
    } catch (err) {
      showToast('Gagal memproses antrian', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleGetWaLink = async (item) => {
    if (!item.customer_phone) {
      showToast('Nomor HP customer tidak tersedia', 'error');
      return;
    }
    try {
      const { data } = await api.get(`/tenant/followup-queue/${item.id}/wa-link`);
      if (data.wa_link) {
        window.open(data.wa_link, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      showToast('Gagal membuat link WA', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
          {toast.message}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Antrian Follow-Up</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola pengiriman pesan follow-up ke customer</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          {stats.due_now > 0 && (
            <Button size="sm" onClick={handleProcessAll} disabled={processing}>
              {processing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />}
              Proses {stats.due_now} Due
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total_pending}</p>
              </div>
              <Clock size={24} className="text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600">Due Now</p>
                <p className="text-2xl font-bold text-amber-700">{stats.due_now}</p>
              </div>
              <AlertCircle size={24} className="text-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600">Terkirim Hari Ini</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.sent_today}</p>
              </div>
              <CheckCircle size={24} className="text-emerald-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Gagal</p>
                <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
              </div>
              <XCircle size={24} className="text-red-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gateway Mode Indicator */}
      <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${hasGateway ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
        <MessageSquare size={14} />
        {hasGateway ? 'Mode Otomatis — Pesan dikirim via Responic' : 'Mode Manual — Klik tombol kirim untuk buka wa.me link'}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
          className="px-3 py-1.5 border border-slate-300 rounded-md text-sm"
        >
          <option value="">Semua Status</option>
          <option value="pending">Pending</option>
          <option value="sent">Terkirim</option>
          <option value="failed">Gagal</option>
          <option value="skipped">Dilewati</option>
          <option value="canceled">Dibatalkan</option>
        </select>
        <select
          value={filterEvent}
          onChange={e => { setFilterEvent(e.target.value); setPage(1); }}
          className="px-3 py-1.5 border border-slate-300 rounded-md text-sm"
        >
          <option value="">Semua Event</option>
          <option value="unpaid_reminder">Belum Bayar</option>
          <option value="post_delivery_review">Review</option>
          <option value="repeat_order">Repeat Order</option>
          <option value="cart_abandoned">Cart Abandoned</option>
        </select>
      </div>

      {/* Queue Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50/50">
                  <th className="text-left p-3 font-medium text-slate-600">Customer</th>
                  <th className="text-left p-3 font-medium text-slate-600">Event</th>
                  <th className="text-left p-3 font-medium text-slate-600">Stage</th>
                  <th className="text-left p-3 font-medium text-slate-600">Jadwal</th>
                  <th className="text-left p-3 font-medium text-slate-600">Status</th>
                  <th className="text-left p-3 font-medium text-slate-600">Pesan</th>
                  <th className="text-right p-3 font-medium text-slate-600">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const evMeta = EVENT_LABELS[item.event] || { label: item.event, color: 'bg-slate-100 text-slate-600' };
                  const stMeta = STATUS_BADGES[item.status] || STATUS_BADGES.pending;
                  const StIcon = stMeta.icon;
                  const canSend = ['pending', 'due', 'failed'].includes(item.status);
                  const isDue = item.status === 'due' || (item.status === 'pending' && new Date(item.scheduled_at) <= new Date());

                  return (
                    <tr key={item.id} className={`border-b last:border-0 hover:bg-slate-50/50 ${isDue ? 'bg-amber-50/30' : ''}`}>
                      <td className="p-3">
                        <p className="font-medium text-slate-800">{item.customer_name || '-'}</p>
                        {item.customer_phone ? (
                          <p className="text-xs text-slate-400">{item.customer_phone}</p>
                        ) : (
                          <p className="text-xs text-red-400">&#9888; No phone</p>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${evMeta.color}`}>{evMeta.label}</span>
                      </td>
                      <td className="p-3 text-slate-600">
                        {item.stage_number}/{item.total_stages}
                      </td>
                      <td className="p-3">
                        <p className="text-slate-700 text-xs">{formatDate(item.scheduled_at)}</p>
                        {canSend && <p className="text-xs text-amber-600">{timeUntil(item.scheduled_at)}</p>}
                        {item.sent_at && <p className="text-xs text-emerald-600">Terkirim: {formatDate(item.sent_at)}</p>}
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${stMeta.color}`}>
                          <StIcon size={12} /> {stMeta.label}
                        </span>
                        {item.sent_via && <p className="text-xs text-slate-400 mt-0.5">via {item.sent_via}</p>}
                      </td>
                      <td className="p-3 max-w-[200px]">
                        <p className="text-xs text-slate-600 truncate">{item.message}</p>
                        <button onClick={() => setExpandedMsg(expandedMsg === item.id ? null : item.id)} className="text-xs text-blue-500 hover:underline">
                          <Eye size={10} className="inline mr-0.5" /> {expandedMsg === item.id ? 'Tutup' : 'Lihat'}
                        </button>
                        {expandedMsg === item.id && (
                          <div className="mt-1 p-2 bg-slate-50 rounded text-xs text-slate-700 whitespace-pre-wrap break-words">
                            {item.message}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {canSend && (
                          <div className="flex items-center gap-1 justify-end">
                            {hasGateway ? (
                              <Button size="sm" variant="default" onClick={() => handleSend(item)} disabled={sendingId === item.id || !item.customer_phone} className="text-xs h-7 px-2" title={!item.customer_phone ? 'Nomor HP tidak tersedia' : 'Kirim via Responic'}>
                                {sendingId === item.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} className="mr-1" />}
                                Kirim
                              </Button>
                            ) : (
                              <Button size="sm" variant="default" onClick={() => handleSend(item)} disabled={sendingId === item.id || !item.customer_phone} className={`text-xs h-7 px-2 ${item.customer_phone ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-300 cursor-not-allowed'}`} title={!item.customer_phone ? 'Nomor HP tidak tersedia' : 'Buka wa.me link'}>
                                {sendingId === item.id ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} className="mr-1" />}
                                WA
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleSkip(item.id)} className="text-xs h-7 px-2 text-slate-500 hover:text-red-600 hover:bg-red-50" title="Lewati follow-up ini">
                              <SkipForward size={12} className="mr-1" /> Skip
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-12 text-center text-slate-400">
                      <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Belum ada follow-up dalam antrian</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 20 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50/50">
              <p className="text-xs text-slate-500">Menampilkan {items.length} dari {total}</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7">Prev</Button>
                <Button variant="outline" size="sm" disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} className="text-xs h-7">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
